import {
  getGetAziendeQueryKey,
  NodoDto,
  useEliminaNodo,
  useGetAziende,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import Typography from "@/elements/Typography";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, TablePagination, TextField, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import NodoDialog from "../NodoDialog";
import { portaleConfig } from "@/portaliConfig";

const columnHelper = createColumnHelper<NodoDto>();

function AziendePage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { isLoading, error, data } = useGetAziende();
  const navigate = useNavigate();

  const {
    filters,
    changeFilter,
    filtersLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePosizioneFilterWithNavigation("/aziende", {
    enablePagination: true,
    enableTextFilter: true,
  });

  const [aziendaDialogState, setAziendaDialogState] = useState({
    open: false,
    azienda: null as NodoDto | null,
  });

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync } = useEliminaNodo({
    mutation: {
      onSuccess: () => {
        toast.success(t("aziendePage.toast.eliminata"));
      },
      onError: () => {
        toast.error(t("errori.richiesta"));
      },
    },
  });

  const queryClient = useQueryClient();

  const deleteAzienda = useCallback(
    async (aziendaId: number) => {
      try {
        await mutateAsync({ id: aziendaId });
        queryClient.invalidateQueries({ queryKey: getGetAziendeQueryKey() });
      } catch (err) {}
    },
    [mutateAsync, queryClient]
  );

  const aziende = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    const filtro = filters.textFilter || "";
    const lowerCaseFiltro = filtro.toLowerCase().replace(/\./g, "");
    return data.data.filter(
      (a) =>
        a.nome.toLowerCase().replace(/\./g, "").indexOf(lowerCaseFiltro) >= 0
    );
  }, [data?.data, filters.textFilter]);

  const aziendePagedData = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return aziende.slice(startIndex, endIndex);
  }, [aziende, page, pageSize]);

  const columns = useMemo(() => {
    const canDelete = hasPermission("AziendaDelete");
    const canEdit = hasPermission("GrafoRead");

    const columns = [
      columnHelper.accessor("nome", {
        header: t("aziendePage.colonne.nome"),
        size: undefined,
      }),
      // columnHelper.display({
      //   header: "",
      //   size: undefined,
      //   cell: ({ row }) => {
      //     return (

      //     );
      //   },
      // }),
      // (canDelete || canEdit) &&
      columnHelper.display({
        id: "buttons",
        header: "",
        // size: canDelete && canEdit ? 350 : canDelete || canEdit ? 250 : 175,
        // maxSize: canDelete && canEdit ? 350 : canDelete || canEdit ? 250 : 175,
        cell: ({ row }) => {
          const hasAccessFromCurrentPortale = !!row.original.portali.find(
            (x) => x.portaleId === portaleConfig.id
          );
          return (
            <Box display="flex" gap={1}>
              <Tooltip
                title={
                  hasAccessFromCurrentPortale
                    ? t("aziendePage.azioni.apri")
                    : t("aziendePage.azioni.apriNonDisponibile", {
                        portale: portaleConfig.portaleNome,
                      })
                }
              >
                <Box>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      navigate(`/nodi/${row.original.id}`);
                    }}
                    disabled={!hasAccessFromCurrentPortale}
                  >
                    <LaunchIcon />
                  </IconButton>
                </Box>
              </Tooltip>

              {canEdit && (
                <IconButton
                  title={t("azioni.modifica")}
                  size="small"
                  color="primary"
                  onClick={() => {
                    navigate(`/azienda/${row.original.id}/grafo`);
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}

              {canDelete && (
                <IconButton
                  title={t("azioni.elimina")}
                  size="small"
                  color="error"
                  onClick={() => {
                    // Controlla se l'azienda ha un target Elaboranet associato
                    if (
                      row.original.elaboranetTargetId ||
                      row.original.elaboranetTarget
                    ) {
                      toast.warn(
                        t("aziendePage.toast.eliminaElaboranet", {
                          nome: row.original.nome,
                        })
                      );
                    } else {
                      askConfirmation({
                        title: t("aziendePage.confirm.titolo"),
                        message: t("aziendePage.confirm.messaggio", {
                          nome: row.original.nome,
                        }),
                        okColor: "error",
                        onConfirm: () => {
                          return deleteAzienda(row.original.id!);
                        },
                      });
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          );
        },
      }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [
    setAziendaDialogState,
    askConfirmation,
    deleteAzienda,
    hasPermission,
    navigate,
    t,
  ]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("aziendePage.titolo")}
        </Typography>
        <Box display="flex" gap={1} pt={1} flexWrap="wrap">
          <TextField
            placeholder={t("aziendePage.filtri.nome")}
            value={filters.textFilter || ""}
            onChange={(e) => changeFilter("textFilter")(null, e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ ml: 1.5 }} /> }}
          />
          {/* {hasPermission("AziendaCreate") && (
            <Button
              size="small"
              color="light"
              onClick={() => {
                setAziendaDialogState({
                  open: true,
                  azienda: null,
                });
              }}
            >
              Aggiungi azienda
            </Button>
          )} */}
        </Box>
      </Box>

      <Table
        columns={columns}
        data={aziendePagedData}
        sortable={true}
        paginated={false}
        initialSort={[{ id: "nome", desc: false }]}
      />

      {aziende.length > 0 && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          pt={2}
        >
          <TablePagination
            component="div"
            page={page}
            count={aziende.length}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            labelRowsPerPage={t("paginazione.righePerPagina")}
            labelDisplayedRows={({ from, to, count }) =>
              count === -1
                ? t("paginazione.righeSenzaTotale", { from, to })
                : t("paginazione.righe", { from, to, count })
            }
          />
        </Box>
      )}

      <NodoDialog
        open={aziendaDialogState.open}
        tipoNodo="Azienda"
        nodoId={aziendaDialogState.azienda?.id}
        onClose={() => {
          setAziendaDialogState((x) => ({
            ...x,
            open: false,
          }));
        }}
      />
      {confirmationDialog}
    </Box>
  );
}

export default AziendePage;
